import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, FileText, Clock, CheckCircle, AlertCircle,
  ChevronRight, BookOpen, Target, Calendar, Award
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getAssignments, getDrafts, createDraft, getMyClasses, getStudentFeedback } from '../lib/api';
import { DraftyMascot } from './DraftyMascot';
import DraftSpace from './DraftSpace';
import Eyebrow from './Eyebrow';
import { toast } from 'sonner';

export const StudentDashboard = ({ showAssignmentsOnly = false }) => {
  const { profile, isDemo } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [hasClasses, setHasClasses] = useState(true);
  const [releasedFeedbackDraftIds, setReleasedFeedbackDraftIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      // Get student's class memberships to scope assignment visibility
      const classData = await getMyClasses();
      const classes = classData.classes || [];
      setHasClasses(classes.length > 0);

      // Collect unique teacher IDs from class memberships
      const teacherIds = [...new Set(classes.map(c => c.teacher_id))];

      // Load assignments from each teacher the student is connected to
      let allAssignments = [];
      for (const teacherId of teacherIds) {
        const teacherAssignments = await getAssignments(teacherId, 'active');
        allAssignments = [...allAssignments, ...teacherAssignments];
      }

      const draftsData = await getDrafts(profile?.id);
      // Ordered by due date (master spec Phase 2 item 4); no-due-date last
      allAssignments.sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });
      setAssignments(allAssignments);
      setDrafts(draftsData || []);

      // Released AI feedback also counts as "Feedback ready" — same
      // released-only filter as CoachPanel (pre-FlowGrade rows have no
      // review_status and are grandfathered released). Non-fatal on failure.
      try {
        const feedbackRows = await getStudentFeedback(profile?.id);
        setReleasedFeedbackDraftIds(new Set(
          (feedbackRows || [])
            .filter(r => (r.review_status ?? 'released') === 'released')
            .map(r => r.draft_id)
        ));
      } catch (feedbackError) {
        setReleasedFeedbackDraftIds(new Set());
      }
    } catch (error) {
      if (isDemo) {
        // Demo users can't fetch real data — show clean empty state
        setAssignments([]);
        setDrafts([]);
      } else {
        console.error('Failed to load data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const startAssignment = async (assignment) => {
    // Check if draft already exists
    const existingDraft = drafts.find(d => d.assignment_id === assignment.id);
    
    if (existingDraft) {
      setSelectedDraft(existingDraft);
      setSelectedAssignment(assignment);
    } else {
      try {
        const newDraft = await createDraft(assignment.id);
        setSelectedDraft(newDraft);
        setSelectedAssignment(assignment);
        setDrafts([...drafts, newDraft]);
        toast.success('Draft created! Start writing.');
      } catch (error) {
        toast.error('Failed to create draft. Try again.');
      }
    }
  };

  // Status cards per master spec Phase 2 item 4:
  // Not started / Drafting / Submitted / Feedback ready
  const getDraftStatus = (assignmentId) => {
    const draft = drafts.find(d => d.assignment_id === assignmentId);
    if (!draft) return { status: 'not_started', label: 'Not started', tone: 'status-chip--gold' };
    if (draft.status === 'submitted' && (draft.teacher_feedback || releasedFeedbackDraftIds.has(draft.id))) {
      return { status: 'feedback_ready', label: 'Feedback ready', tone: 'status-chip--primary' };
    }
    if (draft.status === 'submitted') return { status: 'submitted', label: 'Submitted', tone: 'status-chip--success' };
    return { status: 'drafting', label: 'Drafting', tone: 'status-chip--primary' };
  };

  // If editing a draft, show the editor
  if (selectedDraft && selectedAssignment) {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-to-dashboard"
              onClick={() => {
                setSelectedDraft(null);
                setSelectedAssignment(null);
              }}
              className="p-2 hover:bg-slate-700 rounded-lg text-slate-400"
            >
              <ChevronRight className="rotate-180" size={20} />
            </button>
            <div>
              <Eyebrow label="DRAFT IN PROGRESS" tone="teal" />
              <h2 className="font-heading text-xl font-semibold text-white">
                {selectedAssignment.title}
              </h2>
              <p className="text-slate-400 text-sm">{selectedAssignment.prompt?.slice(0, 100)}...</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedAssignment.ccss_standards?.map((std, idx) => (
              <span key={idx} className="badge-ccss">{std}</span>
            ))}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1">
          <DraftSpace
            draft={selectedDraft}
            assignment={selectedAssignment}
            onUpdate={(data) => {
              // Normalize editor field names to the API draft shape
              const changes = { ...data };
              if (changes.wordCount !== undefined) {
                changes.word_count = changes.wordCount;
                delete changes.wordCount;
              }
              const updated = { ...selectedDraft, ...changes };
              setSelectedDraft(updated);
              // Keep the dashboard's drafts list in sync — otherwise the list
              // shows stale status/content after editing or submitting, and
              // reopening the assignment reloads pre-edit content
              setDrafts(prev => prev.map(d => (d.id === updated.id ? { ...d, ...changes } : d)));
              if (data.content !== undefined) {
              }
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {!showAssignmentsOnly && (
        <>
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-highlight p-6 flex items-center gap-6"
          >
            <DraftyMascot size="lg" mood="excited" />
            <div>
              <h2 className="font-heading text-2xl font-bold text-white">
                Hey {profile?.full_name?.split(' ')[0]}! 👋
              </h2>
              <p className="text-slate-300 mt-2">
                Ready to become a better writer? Let's dive into your assignments!
              </p>
              <p className="text-slate-400 text-sm mt-1">
                Remember: Great writing is rewriting. Take your time and let your ideas flow.
              </p>
            </div>
          </motion.div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-flow/20 rounded-lg">
                  <FileText className="text-teal-flow" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{drafts.length}</p>
                  <p className="text-slate-400 text-sm">Active Drafts</p>
                </div>
              </div>
            </div>
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gold-bright/20 rounded-lg">
                  <Clock className="text-gold-bright" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {drafts.filter(d => d.status === 'in_progress').length}
                  </p>
                  <p className="text-slate-400 text-sm">In Progress</p>
                </div>
              </div>
            </div>
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle className="text-green-500" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {drafts.filter(d => d.status === 'submitted').length}
                  </p>
                  <p className="text-slate-400 text-sm">Submitted</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Assignments */}
      <div>
        <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <BookOpen size={20} className="text-teal-flow" />
          Your Assignments
        </h3>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading assignments...</div>
        ) : assignments.length === 0 ? (
          <div className="card-surface p-8 text-center">
            <DraftyMascot size="md" mood="thinking" className="mx-auto mb-4" />
            {isDemo ? (
              <>
                <p className="text-slate-300 font-medium mb-1">Welcome to the DraftFlow demo!</p>
                <p className="text-slate-400 text-sm">Sign in with Google to see your assignments, write drafts, and get AI coaching feedback.</p>
              </>
            ) : !hasClasses ? (
              <>
                <h3 className="text-white font-heading text-lg mb-2">No classes yet!</h3>
                <p className="text-slate-400 mb-4">Get a class code from your teacher to get started.</p>
                <button
                  data-testid="join-class-from-dashboard"
                  onClick={() => navigate('/join')}
                  className="btn-teal px-6 py-2 rounded-lg"
                >
                  Join a Class
                </button>
              </>
            ) : (
              <>
                <p className="text-slate-400 mb-4">Your assignments will appear here as soon as your teacher posts one. Check back soon!</p>
                <button
                  data-testid="join-another-class"
                  onClick={() => navigate('/join')}
                  className="border border-border-secondary text-cream hover:border-flow-blue-hover px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  Join another class
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {assignments.map((assignment, idx) => {
              const status = getDraftStatus(assignment.id);
              return (
                <motion.div
                  key={assignment.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="card-surface p-5 hover:border-flow-blue/30 transition-colors cursor-pointer group"
                  onClick={() => startAssignment(assignment)}
                  data-testid={`assignment-card-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-heading font-semibold text-white group-hover:text-flow-blue-hover transition-colors">
                          {assignment.title}
                        </h4>
                        <span className={`status-chip ${status.tone}`}>
                          {status.label}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm line-clamp-2">{assignment.prompt}</p>
                      <div className="flex items-center gap-4 mt-3">
                        {assignment.due_date && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Calendar size={12} />
                            Due: {new Date(assignment.due_date).toLocaleDateString()}
                          </span>
                        )}
                        {assignment.ccss_standards?.length > 0 && (
                          <div className="flex gap-1">
                            {assignment.ccss_standards.slice(0, 2).map((std, i) => (
                              <span key={i} className="badge-ccss">{std}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="text-slate-600 group-hover:text-flow-blue-hover transition-colors" size={20} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;
